import {
  Alert,
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import * as Sentry from "@sentry/react";
import { useConfirmOrbitLHandoverMutation } from "Api/Mutations/Merchants/useConfirmOrbitLHandoverMutation";
import { Resources, useResource } from "Translations/Resources";
import { toast } from "sonner";

const RESOURCES = Resources.Components.Merchant.OrbitL.Handover;

type OrbitLHandoverConfirmationProps = {
  merchantPublicID: string;
  merchantTerminalData?: string | null;
};

type TerminalInfo = {
  tid: string;
  serialNumber: string;
  model?: string;
};

export const OrbitLHandoverConfirmation = ({
  merchantPublicID,
  merchantTerminalData,
}: OrbitLHandoverConfirmationProps) => {
  const { t } = useResource();
  const { mutate: confirmHandover, isPending: isConfirming } =
    useConfirmOrbitLHandoverMutation({
      onSuccess: () => toast.success(t(RESOURCES.ConfirmSuccess)),
      onError: () => toast.error(t(RESOURCES.ConfirmErrorGeneric)),
    });

  // Parse terminal data from JSON string
  const terminals: TerminalInfo[] = (() => {
    if (!merchantTerminalData) {
      return [];
    }
    try {
      const data = JSON.parse(merchantTerminalData);
      if (Array.isArray(data)) {
        return data;
      }
      // If it's an object with terminals array
      if (data.terminals && Array.isArray(data.terminals)) {
        return data.terminals;
      }
      return [];
    } catch (error) {
      Sentry.captureException(error, {
        tags: { source: "OrbitLHandoverConfirmation.parseTerminalData" },
      });
      return [];
    }
  })();

  if (terminals.length === 0) {
    return <Alert severity="info">{t(RESOURCES.DispatchedNoData)}</Alert>;
  }

  const handleConfirmHandover = () => {
    confirmHandover({ merchantPublicID });
  };

  const hasModel = terminals.some((t) => t.model);

  return (
    <Stack gap={2}>
      <Typography variant="h6">{t(RESOURCES.Title)}</Typography>

      <Alert severity="success">{t(RESOURCES.DispatchedReady)}</Alert>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>{t(RESOURCES.TableTID)}</strong>
              </TableCell>
              <TableCell>
                <strong>{t(RESOURCES.TableSerialNumber)}</strong>
              </TableCell>
              {hasModel && (
                <TableCell>
                  <strong>{t(RESOURCES.TableModel)}</strong>
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {terminals.map((terminal) => (
              <TableRow key={terminal.tid}>
                <TableCell>{terminal.tid}</TableCell>
                <TableCell>{terminal.serialNumber}</TableCell>
                {hasModel && <TableCell>{terminal.model || "-"}</TableCell>}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="body2" color="text.secondary">
        {t(RESOURCES.Note)}
      </Typography>

      <Button
        variant="contained"
        color="success"
        startIcon={<CheckCircleIcon />}
        onClick={handleConfirmHandover}
        disabled={isConfirming}
        fullWidth
      >
        {isConfirming
          ? t(RESOURCES.ConfirmingButton)
          : t(RESOURCES.ConfirmButton)}
      </Button>
    </Stack>
  );
};
